import { createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { Recipe } from '../types/recipe';
import { getPersistableRecipeId, classifyRecipeId } from '../utils/recipeIds';
import recipeService from '../services/recipeService';
import type { DietaryRole } from '../services/weekGeneratorService';
import type { LufaDeal } from './dealsSlice';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import {
  fetchAllMealPlanWeeksFromCloud,
  MealPlanWeekResult,
} from '../services/mealPlanSyncService';
import { markStartup } from '../utils/startupPerf';
import { getActiveHouseholdId } from '../services/householdContext';
import {
  claimMissReport,
  collectRehydrationTargets,
  confirmDeletedRecipes,
  isConfirmedMiss,
  lookupPlannedRecipe,
} from '../services/recipeRehydration';
import { trackEvent } from '../services/analyticsService';
import { MealEntry, ENTRY_RECIPE_ID, entryTakesSides, isEntryMeal, isSideableEntryMeal } from './mealEntry';
// `currentWeekStart` is aliased: a selector below binds that name to its own
// argument, and a silent shadow in a date helper is how this bug class starts.
import {
  todayIso,
  addDays,
  weekStartOf,
  parseIsoDate,
  currentWeekStart as currentMondayIso,
} from '../utils/localDate';

/**
 * Meal type for scheduling - breakfast only on weekends, lunch and dinner all week
 */
export type MealType = 'breakfast' | 'lunch' | 'dinner';

/** Maximum number of recipes allowed per meal slot (date + mealType) */
export const MAX_MEALS_PER_SLOT = 3;

/** Maximum number of proposals allowed per meal slot — independent budget from accepted meals */
export const MAX_PROPOSALS_PER_SLOT = 2;

// Re-export DietaryRole for convenience
export type { DietaryRole };

// Moved to src/utils/recipeIds.ts so light modules (the enrichment hook, #778)
// can use them without importing the whole slice.
export { getPersistableRecipeId, classifyRecipeId } from '../utils/recipeIds';

/**
 * One household member's opinion on one planned meal.
 *
 * `onRecipeId` is a read-time guard: an opinion counts only while the slot
 * still holds the recipe it was cast on. That makes stale opinions harmless
 * without a migration.
 */
export interface MealApproval {
  value: 'yes' | 'no';
  at: string;          // ISO
  onRecipeId: string;  // recipeId at the time of the vote
}

export interface PlannedMeal {
  id: string;
  recipeId: string;
  recipe?: Recipe; // Denormalized for easy access
  scheduledFor: string; // ISO date string (YYYY-MM-DD)
  dayOfWeek: number; // 0-6, 0 = Sunday
  mealType: MealType; // breakfast, lunch, or dinner
  servings: number;
  notes?: string;
  createdAt: string;
  // Ingredient review tracking
  ingredientsReviewed: boolean; // Has user reviewed ingredients for this meal?
  missingIngredients: string[]; // Ingredients user marked as missing (added to grocery list)
  // Dietary role - if this meal was reserved for a dietary requirement (vegetarian/fish)
  dietaryRole?: DietaryRole;
  // Deals matched to this meal's ingredients
  matchedDeals?: LufaDeal[];
  // Free-text meal (not attached to a recipe)
  freeText?: string;
  // Non-recipe entry (dining / note / leftover / skip) — recipeId === 'entry'
  entry?: MealEntry;
  // Completion tracking
  status?: 'planned' | 'cooked';
  cookedAt?: string; // ISO timestamp when marked done
  /**
   * Portions of this cook kept in the fridge (#663). `undefined` means nobody
   * was ever asked — every meal cooked before the feature shipped. That is not
   * the same as zero: unasked meals stay offerable in the picker without a
   * count, zero means "asked, and there are none left to plan".
   */
  leftoverFridgePortions?: number;
  /** The cook said "no leftovers"; never prompt for this meal again. */
  leftoverPromptDismissed?: boolean;
  rating?: number; // 1-5 star rating
  cookNote?: string; // Free-form "note for next time" captured when logging the cook
  // Who wrote cookNote. The meal plan is shared, so an unattributed note on a
  // paired household reads as the viewer's own — which it often is not.
  cookNoteBy?: string;
  /** Each household member's opinion on THIS meal. Key = uid. */
  approvals?: Record<string, MealApproval>;
  /** uid of the household member cooking this meal. Absent = nobody assigned. */
  cookId?: string;
  /** uid of the household member who created this proposal. Only set for proposals (mode: 'proposed'). */
  createdByUid?: string;
  /** Meal proposal status: 'proposed' = competing recipe awaiting household approval. Absent = accepted. */
  mode?: 'proposed' | 'accepted';
  /** id of the meal this proposal challenges/replaces. Only set on proposals. Used by acceptMeal to remove the incumbent. */
  proposesInsteadOf?: string;
  // Match metadata from generation (why this recipe was selected)
  matchMetadata?: {
    inventoryMatchPercent?: number;
    usesExpiringIngredients?: string[];
    isNewRecipe?: boolean;
    constraintLabel?: string;
    tasteProfileBonus?: number;
    excitementScore?: number;
    dealCount?: number;
    vedetteIngredient?: string;
    ingredientCoverage?: {
      totalCount: number;
      lufaCount: number;
      pantryCount: number;
      toBuyCount: number;
      coveragePercent: number;
      lufaItems: string[];
      pantryItems: string[];
      toBuyItems: string[];
    };
  };
}

/** Which code path produced a `setWeeklyPlan`. Reported as `source` on `week_plan_generated`. */
export type WeekPlanSource =
  /** The planner's empty-week timer decided by itself. */
  | 'auto'
  /** The cook pressed the generate button on the planner. */
  | 'user'
  /** The cook completed the GenerateWizard. */
  | 'wizard'
  /** The cook finished onboarding. */
  | 'onboarding'
  /** The cook flipped a taste axis on the onboarding reading card. */
  | 'onboarding_flip'
  /** The cook asked for the next 7 days at the end of the phone planner (#722). */
  | 'extend';

interface MealPlanState {
  // Meals organized by week start date (Monday) as key
  weeks: Record<string, PlannedMeal[]>;
  // Currently viewed week's start date (Monday)
  currentWeekStart: string;
  loading: boolean;
  error: string | null;
  /**
   * Whether `weeks` reflects the device's own copy yet. False until
   * redux-persist REHYDRATE lands for this slice.
   *
   * `loading` cannot answer this: it starts false and only flips inside the
   * download thunk, so "never fetched" and "fetched, genuinely empty" are the
   * same state to every consumer. An empty week that has simply not hydrated
   * used to read as "this cook has no plan", and TheWeeklyScreen replaced it
   * with a fresh random one.
   */
  localHydrated: boolean;
  /**
   * Whether the Firestore read has settled — fulfilled OR rejected, because a
   * failed read is still an answer. Meaningless for a guest, who has no cloud
   * copy; use `selectMealPlanHydrated`, which folds in auth.
   */
  cloudHydrated: boolean;
}

/**
 * Get the Monday of the current week
 */
function getCurrentMondayStart(): string {
  return currentMondayIso();
}

/**
 * Get today's date as YYYY-MM-DD. The display anchor for the rolling 7-day window.
 */
function getTodayStart(): string {
  return todayIso();
}

const initialState: MealPlanState = {
  weeks: {},
  currentWeekStart: getCurrentMondayStart(),
  loading: false,
  error: null,
  localHydrated: false,
  cloudHydrated: false,
};

/**
 * Helper to generate unique ID
 */
function generateId(): string {
  return `meal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Helper to get day of week from date string
 */
function getDayOfWeek(dateString: string): number {
  return parseIsoDate(dateString).getDay();
}

/**
 * Helper to get start of week (Monday) for a given date
 */
function getWeekStartForDate(dateString: string): string {
  return weekStartOf(dateString);
}

/**
 * Helper to check if date is in a given week
 */
function isInWeek(dateString: string, weekStartDate: string): boolean {
  const date = new Date(dateString + 'T12:00:00');
  const weekStart = new Date(weekStartDate + 'T12:00:00');
  const weekEnd = new Date(weekStart.getTime());
  weekEnd.setDate(weekEnd.getDate() + 7);

  return date >= weekStart && date < weekEnd;
}

/**
 * Remove every meal occupying (scheduledFor, mealType), scanning ALL week
 * buckets — not just the canonical one for `scheduledFor`.
 *
 * Reads (selectCurrentWeekMeals) aggregate meals across every bucket by date
 * range, so a meal can be displayed in a slot while physically living under a
 * bucket key that differs from getWeekStartForDate(scheduledFor) (e.g. legacy
 * or cross-device cloud data bucketed with a different week algorithm). If we
 * only cleared the canonical bucket, such a stale meal would survive a
 * replace/clear and the slot would end up showing two meals — the card keeps
 * the stale one and clicking it opens the wrong recipe.
 *
 * Returns the canonical bucket key so callers can push new slot content there.
 */
function removeSlotMeals(state: MealPlanState, scheduledFor: string, mealType: MealType) {
  takeSlotMeals(state, scheduledFor, mealType);
  return getWeekStartForDate(scheduledFor);
}

/**
 * Same all-buckets sweep as `removeSlotMeals`, but hands back what it removed
 * so the caller can re-file those meals elsewhere (see `swapSlots`).
 */
function takeSlotMeals(
  state: MealPlanState,
  scheduledFor: string,
  mealType: MealType
): PlannedMeal[] {
  const inSlot = (m: PlannedMeal) => m.scheduledFor === scheduledFor && m.mealType === mealType;
  const taken: PlannedMeal[] = [];
  for (const bucketKey of Object.keys(state.weeks)) {
    const bucket = state.weeks[bucketKey];
    if (bucket.some(inSlot)) {
      taken.push(...bucket.filter(inSlot));
      state.weeks[bucketKey] = bucket.filter(m => !inSlot(m));
    }
  }
  return taken;
}

const MEAL_TYPE_ORDER: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };
function sortWeek(meals: PlannedMeal[]) {
  meals.sort((a, b) => a.scheduledFor !== b.scheduledFor
    ? a.scheduledFor.localeCompare(b.scheduledFor)
    : MEAL_TYPE_ORDER[a.mealType] - MEAL_TYPE_ORDER[b.mealType]);
}

/**
 * Download meal plan data from Firestore and merge with local state.
 * Cloud always wins on initial download.
 */
export const downloadMealPlanFromCloud = createAsyncThunk(
  'mealPlan/downloadFromCloud',
  async (userId: string, { getState, rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }

      // Read householdId from auth state so paired users pull from
      // households/{hid}/... instead of users/{uid}/...
      const fullState = getState() as { mealPlan: MealPlanState; auth?: { householdId?: string | null } };
      const householdId = fullState.auth?.householdId ?? null;

      const cloudWeeks = await fetchAllMealPlanWeeksFromCloud(userId, householdId);

      // If user is in a household but the household mealPlan path has no data,
      // fall back to the personal path. This recovers meal plans for household
      // creators whose pre-join data was never migrated to the household scope.
      let effectiveCloudWeeks = cloudWeeks;
      if (householdId && Object.keys(cloudWeeks).length === 0) {
        console.log('⚠️ Household mealPlan empty — falling back to personal path for recovery');
        const personalWeeks = await fetchAllMealPlanWeeksFromCloud(userId, null);
        if (Object.keys(personalWeeks).length > 0) {
          console.log(`🔁 Recovered ${Object.keys(personalWeeks).length} meal plan weeks from personal path`);
          effectiveCloudWeeks = personalWeeks;
        }
      }

      // Get current local state for merging
      const state = fullState;
      const localWeeks = state.mealPlan.weeks;

      // Cloud always wins on initial download — ensures mobile-generated meals
      // appear on other devices. Recipe objects are re-attached from local state
      // (by meal ID) and any still-missing ones are fetched below.
      const mergedWeeks: Record<string, PlannedMeal[]> = { ...localWeeks };

      for (const [weekDate, cloudWeek] of Object.entries(effectiveCloudWeeks)) {
        const localMeals = localWeeks[weekDate] ?? [];
        mergedWeeks[weekDate] = cloudWeek.meals.map(meal => {
          const localMeal = localMeals.find(m => m.id === meal.id);
          return localMeal?.recipe
            ? { ...(meal as PlannedMeal), recipe: localMeal.recipe }
            : (meal as PlannedMeal);
        });

        console.log(`☁️ Using cloud data for week ${weekDate}`);
      }

      // Rehydrate recipe data for meals that came from cloud
      // (cloud meals only have recipeId, not the full recipe object). One
      // lookup per recipe id, misses remembered for the session (#777).
      // Meals are replaced, not mutated: a week the cloud does not hold is
      // still the store's frozen array.
      const targets = collectRehydrationTargets(
        mergedWeeks,
        (meal) => !meal.recipe && meal.recipeId !== 'free-text' && !isEntryMeal(meal)
      );
      const fresh = [...targets.keys()].filter((recipeId) => !isConfirmedMiss(recipeId, userId));
      if (fresh.length > 0) {
        console.log(`🔄 Rehydrating ${fresh.length} recipes from cloud data...`);
        const found = new Map<string, Recipe>();
        await Promise.all(
          fresh.map(async (recipeId) => {
            const recipe = await lookupPlannedRecipe(recipeId, userId);
            if (recipe) found.set(recipeId, recipe);
          })
        );
        if (found.size > 0) {
          for (const weekDate of Object.keys(mergedWeeks)) {
            mergedWeeks[weekDate] = mergedWeeks[weekDate].map((meal) => {
              const recipe = meal.recipe ? undefined : found.get(meal.recipeId);
              return recipe ? { ...meal, recipe } : meal;
            });
          }
        }
        console.log(`✅ Recipe rehydration complete (${found.size}/${fresh.length} found)`);
      }

      return {
        weeks: mergedWeeks,
        // Which weeks the CLOUD actually had. `weeks` above is the union of
        // cloud and local, so it cannot answer "what is the cloud missing" —
        // reading it that way made the post-sign-in adoption a guaranteed
        // no-op, because a guest's own week was always already in it.
        cloudWeekStarts: Object.keys(effectiveCloudWeeks),
        syncTime: new Date().toISOString(),
      };
    } catch (error: any) {
      console.error('Failed to download meal plan from cloud:', error);
      return rejectWithValue(error.message || 'Download failed');
    }
  }
);

/**
 * Push every locally-stored week to Firestore. Use this to recover from
 * a state where local meals were never written to the cloud (e.g. browser
 * was closed before the 1-second debounce sync fired).
 */
/**
 * Push local weeks up.
 *
 * `onlyWeeks` narrows it to a named set. That is what the post-sign-in adoption
 * uses: onboarding builds the first plan while the cook is still a guest, so
 * `mealPlanSyncMiddleware` drops it (`if (!userId) return`) and nothing ever
 * carried it up afterwards — the first plan a new cook was ever shown lived in
 * one browser's IndexedDB and was invisible to every other device, the weekly
 * email and the Skylight push. Scoping the push to weeks the cloud does not
 * have means adoption can only ever add, never overwrite a partner's edit.
 */
export const pushAllWeeksToCloud = createAsyncThunk(
  'mealPlan/pushAllWeeksToCloud',
  async (
    arg: string | { userId: string; onlyWeeks?: string[] },
    { getState, rejectWithValue }
  ) => {
    const userId = typeof arg === 'string' ? arg : arg.userId;
    const onlyWeeks = typeof arg === 'string' ? undefined : arg.onlyWeeks;
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }

      const { syncMealPlanWeekToCloud } = await import('../services/mealPlanSyncService');
      const fullState = getState() as { mealPlan: MealPlanState; auth?: { householdId?: string | null } };
      const householdId = fullState.auth?.householdId ?? null;
      const weeks = fullState.mealPlan.weeks;

      const scope = onlyWeeks ? new Set(onlyWeeks) : null;
      const weekEntries = Object.entries(weeks).filter(
        ([weekStart, meals]) => meals.length > 0 && (!scope || scope.has(weekStart))
      );
      if (weekEntries.length === 0) return 0;
      console.log(`Force-syncing ${weekEntries.length} weeks to cloud...`);

      await Promise.all(
        weekEntries.map(([weekStart, meals]) =>
          syncMealPlanWeekToCloud(
            userId,
            weekStart,
            meals,
            householdId,
          )
        )
      );

      console.log(`Force-sync complete: pushed ${weekEntries.length} weeks`);
      return weekEntries.length;
    } catch (error: any) {
      console.error('Force-sync failed:', error);
      return rejectWithValue(error.message || 'Force-sync failed');
    }
  }
);

/**
 * Re-run recipe rehydration for any meal still missing its `recipe` object.
 *
 * This is the authoritative safety-net pass for `downloadMealPlanFromCloud`.
 * The initial download can miss when it runs before auth/household state is
 * resolved (e.g. the active household id isn't populated yet, so the lookup
 * reads the wrong Firestore scope). Dispatching this AFTER `loadHouseholdId`
 * resolves closes that race deterministically.
 *
 * Because this is the final attempt, a recipe that is STILL unresolvable here
 * is a genuine miss — so this is also where we emit `recipe_rehydrate_failed`.
 * Emitting from the initial download instead would log false positives for the
 * auth-race misses that this very pass recovers.
 */
/**
 * True when an embedded meal recipe carries renderable content. Counts both
 * shapes: `extendedIngredients` (converted docs) and legacy `ingredients`
 * string[]. A truthy-but-content-less stub (as embedded by add-to-plan flows)
 * must NOT count — it blocked rehydration forever, leaving weekly cards,
 * grocery generation, and cook mode without ingredients/steps (#496).
 */
const mealRecipeHasContent = (recipe: Recipe | undefined): boolean => {
  if (!recipe) return false;
  const ingredientCount =
    (recipe.extendedIngredients?.length ?? 0) + (recipe.ingredients?.length ?? 0);
  const instructionCount = Array.isArray(recipe.instructions) ? recipe.instructions.length : 0;
  return ingredientCount > 0 && instructionCount > 0;
};

export const rehydrateMissingRecipes = createAsyncThunk(
  'mealPlan/rehydrateMissingRecipes',
  async (userId: string, { getState, dispatch }) => {
    const fullState = getState() as { mealPlan: MealPlanState };
    const weeks = fullState.mealPlan.weeks;

    const resolved: Array<{ weekStart: string; mealId: string; recipe: Recipe }> = [];
    const missed: string[] = [];
    // One lookup per recipe id, however many meals wait on it, and none for an
    // id this scope already knows is gone (#777). This runs on every live-sync
    // snapshot, so without that it re-asked for every deleted recipe on every
    // write.
    const targets = collectRehydrationTargets(
      weeks,
      (meal) =>
        meal.recipeId !== 'free-text' && !isEntryMeal(meal) && !mealRecipeHasContent(meal.recipe)
    );
    const fresh = [...targets.keys()].filter((recipeId) => !isConfirmedMiss(recipeId, userId));
    if (fresh.length > 0) {
      console.log(`🔄 Retrying rehydration for ${fresh.length} missing recipes...`);
    }

    // This pass is the last word, so it reports — including a miss the boot
    // download already confirmed and therefore never asks about again.
    // `claimMissReport` keeps that to one event per id per scope.
    await Promise.all(
      [...targets].map(async ([recipeId, waiting]) => {
        const recipe = await lookupPlannedRecipe(recipeId, userId);
        if (recipe) {
          for (const target of waiting) resolved.push({ ...target, recipe });
          return;
        }
        missed.push(recipeId);
        if (claimMissReport(recipeId, userId)) {
          trackEvent('recipe_rehydrate_failed', {
            recipe_id: recipeId,
            id_format: classifyRecipeId(recipeId),
            had_user_id: !!userId,
            scope: getActiveHouseholdId() ? 'household' : 'personal',
            meal_count: waiting.length,
          });
        }
      })
    );

    // A meal whose recipe is gone for every member of the plan is removed: it
    // cannot be drawn, yet it counted in the week and held a place in its
    // slot (#777). Only on the server's word — this device cannot see a
    // partner's personal recipes. Through removeFromWeek, so the removal syncs
    // like the cook's own ×.
    const gone = await confirmDeletedRecipes(missed, userId);
    for (const recipeId of gone) {
      const current = (getState() as { mealPlan: MealPlanState }).mealPlan.weeks;
      const mealIds = Object.values(current)
        .flat()
        .filter((meal) => meal.recipeId === recipeId && !isEntryMeal(meal))
        .map((meal) => meal.id);
      for (const mealId of mealIds) dispatch(removeFromWeek(mealId));
      trackEvent('planned_meal_removed_missing_recipe', {
        recipe_id: recipeId,
        id_format: classifyRecipeId(recipeId),
        scope: getActiveHouseholdId() ? 'household' : 'personal',
        meal_count: mealIds.length,
      });
      console.log(`🧹 Removed ${mealIds.length} planned meal(s) whose recipe was deleted: ${recipeId}`);
    }
    return { resolved };
  }
);

const mealPlanSlice = createSlice({
  name: 'mealPlan',
  initialState,
  reducers: {
    /**
     * Set the currently viewed week
     */
    setCurrentWeek: (state, action: PayloadAction<string>) => {
      state.currentWeekStart = action.payload;
    },

    /**
     * Add a meal to a specific week
     */
    addToWeek: (
      state,
      action: PayloadAction<{
        recipe: Recipe;
        scheduledFor: string;
        servings?: number;
        mealType?: MealType;
        /**
         * Overrides the creation stamp. Used when swapping one dish of a slot:
         * inheriting the replaced dish's stamp keeps the new one in the same
         * position in the slot's stack instead of jumping to the end.
         */
        createdAt?: string;
        cookId?: string;
        /**
         * The cook said they cooked it (« Oui, je l'ai cuisinée », #846). The
         * meal lands cooked, dated on its own day. Nothing else makes a new
         * meal cooked: a meal on a day that is over is still only planned.
         */
        cooked?: boolean;
      }>
    ) => {
      const { recipe, scheduledFor, servings = 4, mealType = 'dinner', createdAt, cookId, cooked } = action.payload;

      // Guard: bail out if recipe or its id is missing
      if (!recipe || !recipe.id) return;

      // Cache the recipe so it can be retrieved later
      try {
        recipeService.cacheRecipe(recipe);
      } catch (_e) {
        // Don't let cache failure crash the state update
      }

      // Determine which week this meal belongs to
      const weekStart = getWeekStartForDate(scheduledFor);

      // Initialize week array if needed
      if (!state.weeks[weekStart]) {
        state.weeks[weekStart] = [];
      }

      const recipeIdStr = getPersistableRecipeId(recipe);

      // Check if this exact recipe is already planned for this day and meal type
      const existing = state.weeks[weekStart].find(
        meal => meal.recipeId === recipeIdStr && meal.scheduledFor === scheduledFor && meal.mealType === mealType
      );

      if (existing) {
        // Update servings if already exists
        existing.servings = servings;
      } else {
        // Max 3 accepted recipes per slot — reject if already at capacity. Proposals don't count toward this cap.
        const slotCount = state.weeks[weekStart].filter(
          meal => meal.scheduledFor === scheduledFor && meal.mealType === mealType && meal.mode !== 'proposed'
        ).length;
        if (slotCount >= MAX_MEALS_PER_SLOT) return;

        // Add new planned meal
        const newMeal: PlannedMeal = {
          id: generateId(),
          recipeId: recipeIdStr,
          recipe,
          scheduledFor,
          dayOfWeek: getDayOfWeek(scheduledFor),
          mealType,
          servings,
          createdAt: createdAt ?? new Date().toISOString(),
          ingredientsReviewed: false,
          missingIngredients: [],
          ...(cookId ? { cookId } : {}),
        };

        // Cooked only when the cook says so. A meal put on a day that is over
        // used to be stamped cooked, dated at the tap: a Wednesday tapped by
        // mistake on Friday morning read « Cuisiné 1 fois · dernière fois le
        // 23 octobre » on a dinner nobody had made (#846). The « Planifier »
        // sheet asks on a past day and passes `cooked` when the answer is yes.
        if (cooked) {
          newMeal.status = 'cooked';
          newMeal.cookedAt = parseIsoDate(scheduledFor).toISOString();
        }

        state.weeks[weekStart].push(newMeal);

        // Sort by date, then by meal type order (breakfast, lunch, dinner)
        const mealTypeOrder: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };
        state.weeks[weekStart].sort((a, b) => {
          if (a.scheduledFor !== b.scheduledFor) {
            return a.scheduledFor.localeCompare(b.scheduledFor);
          }
          return mealTypeOrder[a.mealType] - mealTypeOrder[b.mealType];
        });
      }
    },

    /**
     * Add a free-text meal (not attached to a recipe)
     */
    addFreeTextMeal: (
      state,
      action: PayloadAction<{
        text: string;
        scheduledFor: string;
        mealType: MealType;
      }>
    ) => {
      const { text, scheduledFor, mealType } = action.payload;
      const weekStart = getWeekStartForDate(scheduledFor);

      if (!state.weeks[weekStart]) {
        state.weeks[weekStart] = [];
      }

      // Max 3 accepted recipes per slot — reject if already at capacity. Proposals don't count toward this cap.
      const slotCount = state.weeks[weekStart].filter(
        meal => meal.scheduledFor === scheduledFor && meal.mealType === mealType && meal.mode !== 'proposed'
      ).length;
      if (slotCount >= MAX_MEALS_PER_SLOT) return;

      const newMeal: PlannedMeal = {
        id: generateId(),
        recipeId: 'free-text',
        scheduledFor,
        dayOfWeek: getDayOfWeek(scheduledFor),
        mealType,
        servings: 1,
        createdAt: new Date().toISOString(),
        ingredientsReviewed: true,
        missingIngredients: [],
        freeText: text,
      };

      state.weeks[weekStart].push(newMeal);

      // Sort by date, then by meal type order
      const mealTypeOrder: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };
      state.weeks[weekStart].sort((a, b) => {
        if (a.scheduledFor !== b.scheduledFor) {
          return a.scheduledFor.localeCompare(b.scheduledFor);
        }
        return mealTypeOrder[a.mealType] - mealTypeOrder[b.mealType];
      });
    },

    /**
     * Remove a meal by ID (searches all weeks)
     */
    removeFromWeek: (state, action: PayloadAction<string>) => {
      const mealId = action.payload;
      // Find and remove from whichever week contains it
      for (const weekStart of Object.keys(state.weeks)) {
        const index = state.weeks[weekStart].findIndex(meal => meal.id === mealId);
        if (index !== -1) {
          state.weeks[weekStart].splice(index, 1);
          break;
        }
      }
    },

    /**
     * Update notes for a meal
     */
    updateMealNotes: (
      state,
      action: PayloadAction<{ mealId: string; notes: string }>
    ) => {
      const { mealId, notes } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          meal.notes = notes;
          break;
        }
      }
    },

    /**
     * Update servings for a meal
     */
    updateMealServings: (
      state,
      action: PayloadAction<{ mealId: string; servings: number }>
    ) => {
      const { mealId, servings } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          meal.servings = servings;
          break;
        }
      }
    },

    /**
     * Clear all meals for a specific week
     */
    clearWeek: (state, action: PayloadAction<string | undefined>) => {
      const weekStart = action.payload || state.currentWeekStart;
      state.weeks[weekStart] = [];
    },

    /**
     * Remove weeks older than a certain threshold
     */
    cleanupOldMeals: (state) => {
      // Keep only weeks from 2 weeks ago onwards
      const cutoffString = addDays(getCurrentMondayStart(), -14);

      for (const weekStart of Object.keys(state.weeks)) {
        if (weekStart < cutoffString) {
          delete state.weeks[weekStart];
        }
      }
    },

    /**
     * Update recipe data for all meals with a given recipe ID
     */
    updateMealRecipe: (
      state,
      action: PayloadAction<{ recipeId: string; recipe: Recipe }>
    ) => {
      const { recipeId, recipe } = action.payload;

      // Find all meals with this recipe across all weeks and update them
      for (const weekStart of Object.keys(state.weeks)) {
        state.weeks[weekStart].forEach(meal => {
          if (meal.recipeId === recipeId) {
            meal.recipe = recipe;
          }
        });
      }

      // Also cache the updated recipe
      recipeService.cacheRecipe(recipe);
    },

    /**
     * Set all meals for a week at once (used by week generator)
     * Clears any existing confirmation - user must re-confirm after regenerating
     */
    setWeeklyPlan: (
      state,
      action: PayloadAction<{
        weekStartDate: string;
        meals: PlannedMeal[];
        /**
         * Who asked. Carried purely so `week_plan_generated` can tell a plan
         * the cook requested from one the app decided on by itself — without
         * it, a silent replacement and a button press are the same row in
         * PostHog, which is why this class of bug went unreported for weeks.
         */
        source?: WeekPlanSource;
        /**
         * How many of `meals` this write added. An `extend` write carries the
         * bucket's existing meals too — it adds, it never replaces — so the
         * array length would count dinners nobody generated.
         */
        addedCount?: number;
      }>
    ) => {
      const { weekStartDate, meals } = action.payload;

      // Cache all recipes
      meals.forEach(meal => {
        if (meal.recipe) {
          recipeService.cacheRecipe(meal.recipe);
        }
      });

      // Group meals by their actual week (meals may span multiple weeks)
      const mealsByWeek = new Map<string, PlannedMeal[]>();
      meals.forEach(meal => {
        const weekStart = getWeekStartForDate(meal.scheduledFor);
        if (!mealsByWeek.has(weekStart)) {
          mealsByWeek.set(weekStart, []);
        }
        mealsByWeek.get(weekStart)!.push(meal);
      });

      // Set meals for each affected week
      const mealTypeOrder: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };
      mealsByWeek.forEach((weekMeals, weekStart) => {
        state.weeks[weekStart] = weekMeals;
        state.weeks[weekStart].sort((a, b) => {
          if (a.scheduledFor !== b.scheduledFor) {
            return a.scheduledFor.localeCompare(b.scheduledFor);
          }
          return mealTypeOrder[a.mealType] - mealTypeOrder[b.mealType];
        });
      });
    },

    /**
     * Replace a meal with a new recipe (for double-tap replacement)
     */
    replaceMeal: (
      state,
      action: PayloadAction<{ mealId: string; newRecipe: Recipe }>
    ) => {
      const { mealId, newRecipe } = action.payload;

      // Cache the new recipe
      recipeService.cacheRecipe(newRecipe);

      // Find the meal and replace its recipe
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          const newRecipeId = getPersistableRecipeId(newRecipe);
          if (meal.recipeId !== newRecipeId) {
            // A different dish has not been cooked. Keeping the old cook on the
            // meal credited the new recipe with it — and its note, which the
            // journal would then have filed under the wrong recipe (#795). The
            // old dish's note is already in its own journal.
            meal.status = 'planned';
            delete meal.cookedAt;
            delete meal.cookNote;
            delete meal.cookNoteBy;
            delete meal.rating;
            delete meal.leftoverFridgePortions;
            delete meal.leftoverPromptDismissed;
          }
          meal.recipeId = newRecipeId;
          meal.recipe = newRecipe;
          // Reset ingredient review since it's a new recipe
          meal.ingredientsReviewed = false;
          meal.missingIngredients = [];
          break;
        }
      }
    },

    /**
     * Move a meal to a different slot (for drag-drop reordering)
     */
    moveMeal: (
      state,
      action: PayloadAction<{
        mealId: string;
        newScheduledFor: string;
        newMealType: MealType;
      }>
    ) => {
      const { mealId, newScheduledFor, newMealType } = action.payload;

      // Find the meal to move
      let mealToMove: PlannedMeal | undefined;
      let sourceWeekStart: string | undefined;
      let sourceIndex: number = -1;

      for (const weekStart of Object.keys(state.weeks)) {
        const index = state.weeks[weekStart].findIndex(m => m.id === mealId);
        if (index !== -1) {
          mealToMove = state.weeks[weekStart][index];
          sourceWeekStart = weekStart;
          sourceIndex = index;
          break;
        }
      }

      if (!mealToMove || !sourceWeekStart) return;

      // Determine destination week
      const destWeekStart = getWeekStartForDate(newScheduledFor);

      // Initialize destination week if needed
      if (!state.weeks[destWeekStart]) {
        state.weeks[destWeekStart] = [];
      }

      // Count meals already in destination slot
      const destSlotCount = state.weeks[destWeekStart].filter(
        m => m.scheduledFor === newScheduledFor && m.mealType === newMealType
      ).length;

      // Reject if destination slot already has 3 meals
      if (destSlotCount >= MAX_MEALS_PER_SLOT) return;

      // Remove meal from source
      state.weeks[sourceWeekStart].splice(sourceIndex, 1);

      // Update the moved meal with new position
      mealToMove.scheduledFor = newScheduledFor;
      mealToMove.mealType = newMealType;
      mealToMove.dayOfWeek = getDayOfWeek(newScheduledFor);

      // Add to destination
      state.weeks[destWeekStart].push(mealToMove);

      // Sort both affected weeks
      const mealTypeOrder: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };
      const sortMeals = (meals: PlannedMeal[]) => {
        meals.sort((a, b) => {
          if (a.scheduledFor !== b.scheduledFor) {
            return a.scheduledFor.localeCompare(b.scheduledFor);
          }
          return mealTypeOrder[a.mealType] - mealTypeOrder[b.mealType];
        });
      };

      sortMeals(state.weeks[destWeekStart]);
      if (sourceWeekStart !== destWeekStart && state.weeks[sourceWeekStart]) {
        sortMeals(state.weeks[sourceWeekStart]);
      }
    },

    /**
     * Mark ingredients as reviewed for a meal
     */
    markIngredientsReviewed: (
      state,
      action: PayloadAction<{ mealId: string; missingIngredients: string[] }>
    ) => {
      const { mealId, missingIngredients } = action.payload;

      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          meal.ingredientsReviewed = true;
          meal.missingIngredients = missingIngredients;
          break;
        }
      }
    },

    /**
     * Mark a meal as cooked/done
     */
    markMealAsCooked: (
      state,
      action: PayloadAction<{
        mealId: string;
        cookedAt?: string;
        note?: string;
        /** Who is writing the note — captured at write time, never inferred. */
        noteBy?: string;
      }>
    ) => {
      const { mealId, cookedAt, note, noteBy } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          meal.status = 'cooked';
          // Default the cook stamp to the meal's planned day (noon-anchored to
          // avoid a UTC-midnight day-shift), not "now" — marking a meal planned
          // for the 18th done on the 20th should record the 18th. Callers (e.g.
          // the recipe-detail capture sheet logging a specific past date) may
          // still override via cookedAt.
          meal.cookedAt =
            cookedAt ??
            (meal.scheduledFor
              ? new Date(meal.scheduledFor + 'T12:00:00').toISOString()
              : new Date().toISOString());
          // Preserve any free-form note captured with the cook. Only overwrite
          // when a note is supplied so a note-less re-mark doesn't wipe it.
          if (note !== undefined) {
            meal.cookNote = note;
            // Attribution travels with the note, including when it is cleared:
            // leaving a stale author on an empty note would credit the wrong
            // person for the next one.
            if (noteBy) meal.cookNoteBy = noteBy;
            else delete meal.cookNoteBy;
          }
          break;
        }
      }
    },

    /**
     * Record how many portions of a cook went in the fridge (#663).
     *
     * `dismissed` is the "no leftovers" answer. It is stored rather than
     * inferred from `portions: 0` because the two are different questions:
     * zero means "asked, none left to plan", undefined means "never asked", and
     * only the second should ever prompt again.
     */
    setLeftoverPortions: (
      state,
      action: PayloadAction<{ mealId: string; fridgePortions?: number; dismissed?: boolean }>
    ) => {
      const { mealId, fridgePortions, dismissed } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          if (fridgePortions !== undefined) {
            meal.leftoverFridgePortions = Math.max(0, Math.floor(fridgePortions));
          }
          if (dismissed !== undefined) {
            meal.leftoverPromptDismissed = dismissed;
          }
          break;
        }
      }
    },

    /**
     * Unmark a meal as cooked (toggle back to planned)
     */
    unmarkMealAsCooked: (
      state,
      action: PayloadAction<{ mealId: string }>
    ) => {
      const { mealId } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          meal.status = 'planned';
          meal.cookedAt = undefined;
          break;
        }
      }
    },

    /**
     * Rate a meal (1-5 stars)
     */
    rateMeal: (
      state,
      action: PayloadAction<{ mealId: string; rating: number }>
    ) => {
      const { mealId, rating } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (meal) {
          meal.rating = rating;
          break;
        }
      }
    },

    /**
     * Record, change, or retract one member's opinion on a meal.
     *
     * Keyed by uid so two offline writes merge without loss: last-write-wins
     * applies per key, not per object, so neither member can erase the other.
     */
    setMealApproval: (
      state,
      action: PayloadAction<{ mealId: string; uid: string; value: 'yes' | 'no' | null }>
    ) => {
      const { mealId, uid, value } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (!meal) continue;
        if (value === null) {
          if (meal.approvals) delete meal.approvals[uid];
        } else {
          if (!meal.approvals) meal.approvals = {};
          meal.approvals[uid] = { value, at: new Date().toISOString(), onRecipeId: meal.recipeId };
        }
        break;
      }
    },

    /**
     * Assign, reassign, or clear who cooks a meal.
     *
     * Deleting the key rather than storing null keeps the field absent in
     * Firestore, which is what "nobody assigned" means everywhere else.
     */
    setMealCook: (
      state,
      action: PayloadAction<{ mealId: string; uid: string | null }>
    ) => {
      const { mealId, uid } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (!meal) continue;
        if (uid === null) delete meal.cookId;
        else meal.cookId = uid;
        break;
      }
    },

    /**
     * Create a proposal: add a recipe to a slot as 'proposed', with the author's approval.
     *
     * Proposals have an independent budget (MAX_PROPOSALS_PER_SLOT) so planning 3 main
     * dishes doesn't lock out proposing. The author's vote counts as real, stored approval
     * so the partner can see it and the author can change their mind without withdrawing.
     */
    proposeMeal: (
      state,
      action: PayloadAction<{
        recipe: Recipe;
        scheduledFor: string;
        mealType: MealType;
        uid: string;
        proposesInsteadOf?: string;
      }>
    ) => {
      const { recipe, scheduledFor, mealType, uid, proposesInsteadOf } = action.payload;
      const weekStart = getWeekStartForDate(scheduledFor);

      if (!state.weeks[weekStart]) {
        state.weeks[weekStart] = [];
      }

      // Max 2 proposals per slot — reject if already at capacity
      const proposalCount = state.weeks[weekStart].filter(
        meal => meal.scheduledFor === scheduledFor && meal.mealType === mealType && meal.mode === 'proposed'
      ).length;
      if (proposalCount >= MAX_PROPOSALS_PER_SLOT) return;

      const recipeIdStr = getPersistableRecipeId(recipe);

      const newMeal: PlannedMeal = {
        id: generateId(),
        recipeId: recipeIdStr,
        recipe,
        scheduledFor,
        dayOfWeek: getDayOfWeek(scheduledFor),
        mealType,
        servings: 1,
        createdAt: new Date().toISOString(),
        ingredientsReviewed: false,
        missingIngredients: [],
        mode: 'proposed',
        createdByUid: uid,
        proposesInsteadOf,
        approvals: {
          [uid]: {
            value: 'yes',
            at: new Date().toISOString(),
            onRecipeId: recipeIdStr,
          },
        },
      };

      state.weeks[weekStart].push(newMeal);

      // Sort by date, then by meal type order (breakfast, lunch, dinner)
      sortWeek(state.weeks[weekStart]);
    },

    /**
     * Accept a proposal: flip mode to accepted, optionally assign a cook, and remove the meal it challenged.
     *
     * When a proposal with proposesInsteadOf is accepted:
     * 1. The proposal becomes accepted.
     * 2. The meal it challenged is removed from the slot.
     * 3. Sibling proposals that challenged the same meal are also removed (the question is decided).
     * 4. Anything else (side dishes, proposals challenging different meals) is untouched.
     *
     * If proposesInsteadOf is absent (old proposal or empty-slot proposal), the proposal just accepts.
     * Sets cookId only if it was absent.
     */
    acceptMeal: (
      state,
      action: PayloadAction<{ mealId: string; uid: string }>
    ) => {
      const { mealId, uid } = action.payload;
      for (const weekStart of Object.keys(state.weeks)) {
        const meal = state.weeks[weekStart].find(m => m.id === mealId);
        if (!meal) continue;

        // Flip mode to accepted (removing 'proposed'), optionally assign cook
        if (meal.mode === 'proposed') {
          meal.mode = undefined;
        }
        if (!meal.cookId) {
          meal.cookId = uid;
        }

        // Remove the meal this proposal challenged, and any sibling proposals challenging it
        if (meal.proposesInsteadOf) {
          state.weeks[weekStart] = state.weeks[weekStart].filter(m => {
            // Keep all meals except:
            // 1. The meal being replaced
            // 2. Other proposals that challenged the same meal
            if (m.id === meal.proposesInsteadOf) return false; // Remove the incumbent
            if (m.mode === 'proposed' && m.proposesInsteadOf === meal.proposesInsteadOf) return false; // Remove siblings
            return true; // Keep everything else
          });
        }

        break;
      }
    },

    /**
     * Apply incoming Firestore snapshot — used by the live-sync hook.
     * Replaces weeks; preserves currentWeekStart so the user
     * doesn't get yanked off the week they're viewing.
     */
    hydrateMealPlanFromCloud: (
      state,
      action: PayloadAction<{ weeks: Record<string, PlannedMeal[]> }>
    ) => {
      state.weeks = action.payload.weeks;
    },
    /**
     * Non-destructive merge from a live Firestore snapshot.
     * Preserves local-only weeks (not yet synced) and re-attaches recipe objects
     * that Firestore strips on write, so the UI never goes blank mid-session.
     */
    mergeMealPlanFromLiveSync: (
      state,
      action: PayloadAction<{ weeks: Record<string, PlannedMeal[]> }>
    ) => {
      const { weeks: cloudWeeks } = action.payload;
      // The onSnapshot listener has produced a snapshot, so the cloud has
      // answered even if the one-shot download thunk is still in flight.
      state.cloudHydrated = true;
      const merged: Record<string, PlannedMeal[]> = { ...state.weeks };
      for (const [weekStart, cloudMeals] of Object.entries(cloudWeeks)) {
        const localMeals = state.weeks[weekStart] ?? [];
        merged[weekStart] = cloudMeals.map((cloudMeal) => {
          const localMeal = localMeals.find((m) => m.id === cloudMeal.id);
          return localMeal?.recipe ? { ...cloudMeal, recipe: localMeal.recipe } : cloudMeal;
        });
      }
      state.weeks = merged;
    },

    /**
     * Put a non-recipe entry in a slot, replacing whatever was there — except
     * the sides of a note or leftovers being rewritten as another note or
     * leftovers: fixing a typo in « Porcs Sevelin BBQ » must not delete the
     * salad beside it.
     */
    setSlotEntry: (state, action: PayloadAction<{ entry: MealEntry; scheduledFor: string; mealType: MealType }>) => {
      const { entry, scheduledFor, mealType } = action.payload;
      const taken = takeSlotMeals(state, scheduledFor, mealType);
      const weekStart = getWeekStartForDate(scheduledFor);
      if (!state.weeks[weekStart]) state.weeks[weekStart] = [];
      if (entryTakesSides(entry) && taken.some(isSideableEntryMeal)) {
        state.weeks[weekStart].push(...taken.filter((m) => !isEntryMeal(m)));
      }
      const meal: PlannedMeal = {
        id: generateId(),
        recipeId: ENTRY_RECIPE_ID,
        entry,
        scheduledFor,
        dayOfWeek: getDayOfWeek(scheduledFor),
        mealType,
        servings: 1,
        createdAt: new Date().toISOString(),
        ingredientsReviewed: true,
        missingIngredients: [],
      };
      state.weeks[weekStart].push(meal);
      sortWeek(state.weeks[weekStart]);
    },

    /** Empty a slot entirely (recipes and entries). */
    clearSlot: (state, action: PayloadAction<{ scheduledFor: string; mealType: MealType }>) => {
      removeSlotMeals(state, action.payload.scheduledFor, action.payload.mealType);
    },

    /**
     * Exchange two slots' contents (drag-and-drop swap, and the move picker's
     * "Échanger"). Handles empty sides.
     *
     * The two slots may sit in different weeks: the move picker can browse to
     * another week, so both meals are lifted out of whatever bucket holds them
     * and re-filed under the bucket their new date belongs to. Mutating in
     * place inside one week's array — what this used to do — left a swapped
     * meal filed under the wrong week, where the next cloud sync would drop it.
     */
    swapSlots: (state, action: PayloadAction<{
      a: { scheduledFor: string; mealType: MealType };
      b: { scheduledFor: string; mealType: MealType };
    }>) => {
      const { a, b } = action.payload;
      if (a.scheduledFor === b.scheduledFor && a.mealType === b.mealType) return;

      // Lift both sides before re-filing either, so the second take can't
      // scoop up the meals the first one just moved into its slot.
      const fromA = takeSlotMeals(state, a.scheduledFor, a.mealType);
      const fromB = takeSlotMeals(state, b.scheduledFor, b.mealType);

      const touched = new Set<string>();
      const fileInto = (meals: PlannedMeal[], dest: { scheduledFor: string; mealType: MealType }) => {
        if (meals.length === 0) return;
        const weekStart = getWeekStartForDate(dest.scheduledFor);
        if (!state.weeks[weekStart]) state.weeks[weekStart] = [];
        meals.forEach((m) => {
          m.scheduledFor = dest.scheduledFor;
          m.mealType = dest.mealType;
          m.dayOfWeek = getDayOfWeek(dest.scheduledFor);
          state.weeks[weekStart].push(m);
        });
        touched.add(weekStart);
      };

      fileInto(fromA, b);
      fileInto(fromB, a);
      touched.forEach((weekStart) => sortWeek(state.weeks[weekStart]));
    },

    /** Replace a slot's content with a recipe (generation, fan swap, library-to-slot). */
    replaceSlotRecipe: (state, action: PayloadAction<{ recipe: Recipe; scheduledFor: string; servings?: number; mealType: MealType }>) => {
      const { recipe, scheduledFor, servings = 4, mealType } = action.payload;
      if (!recipe || !recipe.id) return;
      try { recipeService.cacheRecipe(recipe); } catch (_e) { /* non-fatal */ }
      const weekStart = removeSlotMeals(state, scheduledFor, mealType);
      if (!state.weeks[weekStart]) state.weeks[weekStart] = [];
      state.weeks[weekStart].push({
        id: generateId(),
        recipeId: getPersistableRecipeId(recipe),
        recipe,
        scheduledFor,
        dayOfWeek: getDayOfWeek(scheduledFor),
        mealType,
        servings,
        createdAt: new Date().toISOString(),
        ingredientsReviewed: false,
        missingIngredients: [],
      });
      sortWeek(state.weeks[weekStart]);
    },

    /** Restore a full week snapshot (undo). */
    restoreWeek: (state, action: PayloadAction<{ weekStart: string; meals: PlannedMeal[] }>) => {
      state.weeks[action.payload.weekStart] = action.payload.meals;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      // mealPlan is a nested persist (key === 'mealPlan'), so its REHYDRATE
      // payload IS the slice state. Fall back to the legacy root-persist shape
      // (action.payload.mealPlan) for backwards compatibility.
      const incoming = action.key === 'mealPlan' ? action.payload : action.payload?.mealPlan;

      // The disk has answered for this slice. A missing payload is an answer
      // too — a first-run profile has nothing stored — so this is set before
      // any of the shape checks below, not inside them. REHYDRATE fires once
      // per persist key, so only this slice's own key (or the legacy root
      // shape) may flip it.
      if (action.key === 'mealPlan' || action.payload?.mealPlan !== undefined) {
        state.localHydrated = true;
      }

      // MIGRATION: Convert old thisWeek format to new weeks format
      if (incoming?.thisWeek && !incoming?.weeks) {
        console.log('MealPlan - Migrating from old thisWeek format to new weeks format');
        const oldMeals = incoming.thisWeek as any[];
        const migratedWeeks: Record<string, PlannedMeal[]> = {};

        oldMeals.forEach((oldMeal: any) => {
          // Determine week start for this meal
          const weekStart = getWeekStartForDate(oldMeal.scheduledFor);

          // Initialize week array if needed
          if (!migratedWeeks[weekStart]) {
            migratedWeeks[weekStart] = [];
          }

          // Create migrated meal with new fields
          const migratedMeal: PlannedMeal = {
            id: oldMeal.id || generateId(),
            recipeId: oldMeal.recipeId || (oldMeal.recipe ? getPersistableRecipeId(oldMeal.recipe) : undefined),
            recipe: oldMeal.recipe,
            scheduledFor: oldMeal.scheduledFor,
            dayOfWeek: oldMeal.dayOfWeek ?? getDayOfWeek(oldMeal.scheduledFor),
            mealType: oldMeal.mealType || 'dinner', // Default to dinner for legacy meals
            servings: oldMeal.servings || 4,
            notes: oldMeal.notes,
            createdAt: oldMeal.createdAt || new Date().toISOString(),
            ingredientsReviewed: oldMeal.ingredientsReviewed ?? false,
            missingIngredients: oldMeal.missingIngredients || [],
          };

          migratedWeeks[weekStart].push(migratedMeal);

          // Cache recipe if available
          if (migratedMeal.recipe) {
            recipeService.cacheRecipe(migratedMeal.recipe);
          }
        });

        // Sort meals in each week
        const mealTypeOrder: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };
        for (const weekStart of Object.keys(migratedWeeks)) {
          migratedWeeks[weekStart].sort((a, b) => {
            if (a.scheduledFor !== b.scheduledFor) {
              return a.scheduledFor.localeCompare(b.scheduledFor);
            }
            return mealTypeOrder[a.mealType] - mealTypeOrder[b.mealType];
          });
        }

        // Update state with migrated data
        state.weeks = migratedWeeks;
        state.currentWeekStart = getCurrentMondayStart();

        console.log(`MealPlan - Migrated ${oldMeals.length} meals to ${Object.keys(migratedWeeks).length} weeks`);
        return;
      }

      // When app rehydrates from Redux Persist, re-cache all recipes from all weeks
      if (incoming?.weeks) {
        markStartup('mealPlan REHYDRATE: cache loop start');
        const weeks = incoming.weeks as Record<string, PlannedMeal[]>;
        let totalCached = 0;
        for (const weekStart of Object.keys(weeks)) {
          weeks[weekStart].forEach(meal => {
            if (meal.recipe) {
              recipeService.cacheRecipe(meal.recipe);
              totalCached++;
            }
          });
        }
        markStartup(`mealPlan REHYDRATE: cache loop done (${totalCached} recipes)`);
        console.log(`MealPlan - Re-cached ${totalCached} recipes on rehydrate`);
      }
    });

    // Handle downloadMealPlanFromCloud async thunk
    builder
      .addCase(downloadMealPlanFromCloud.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadMealPlanFromCloud.fulfilled, (state, action) => {
        state.loading = false;
        state.cloudHydrated = true;
        // Non-destructive merge: cloud meals win on conflict (by ID), but local-only
        // meals are preserved. This handles two cases:
        //   1. Meals added by the live onSnapshot listener between thunk start and
        //      fulfillment that aren't yet in the cloud snapshot.
        //   2. Locally-added meals still awaiting the debounce sync to Firestore.
        const merged: Record<string, PlannedMeal[]> = { ...state.weeks };
        for (const [weekStart, cloudMeals] of Object.entries(action.payload.weeks)) {
          const currentMeals = state.weeks[weekStart] ?? [];
          const mappedCloudMeals = (cloudMeals as PlannedMeal[]).map(cloudMeal => {
            const currentMeal = currentMeals.find(m => m.id === cloudMeal.id);
            return currentMeal?.recipe ? { ...cloudMeal, recipe: currentMeal.recipe } : cloudMeal;
          });
          const cloudIds = new Set((cloudMeals as PlannedMeal[]).map(m => m.id));
          const localOnlyMeals = currentMeals.filter(m => !cloudIds.has(m.id));
          merged[weekStart] = [...mappedCloudMeals, ...localOnlyMeals];
        }
        state.weeks = merged;
        console.log(`MealPlan - Synced ${Object.keys(action.payload.weeks).length} weeks from cloud`);
      })
      .addCase(downloadMealPlanFromCloud.rejected, (state, action) => {
        state.loading = false;
        // A failed read is still a settled read. Leaving this false would park
        // the planner on a permanent "not loaded yet" and never let an offline
        // cook generate a week at all.
        state.cloudHydrated = true;
        state.error = action.payload as string;
        console.error('MealPlan - Cloud sync failed:', action.payload);
      })
      .addCase(rehydrateMissingRecipes.fulfilled, (state, action) => {
        let recovered = 0;
        for (const { weekStart, mealId, recipe } of action.payload.resolved) {
          const meal = state.weeks[weekStart]?.find(m => m.id === mealId);
          if (meal) {
            meal.recipe = recipe;
            // Self-heal the stored id to its canonical form so the meal stays
            // resolvable on subsequent reads / re-syncs.
            meal.recipeId = getPersistableRecipeId(recipe);
            recovered++;
          }
        }
        if (recovered > 0) {
          console.log(`MealPlan - Recovered ${recovered} previously-missing recipes`);
        }
      });
  },
});

export const {
  setCurrentWeek,
  addToWeek,
  addFreeTextMeal,
  removeFromWeek,
  updateMealNotes,
  updateMealServings,
  clearWeek,
  cleanupOldMeals,
  updateMealRecipe,
  setWeeklyPlan,
  replaceMeal,
  moveMeal,
  markIngredientsReviewed,
  markMealAsCooked,
  setLeftoverPortions,
  unmarkMealAsCooked,
  rateMeal,
  setMealApproval,
  setMealCook,
  proposeMeal,
  acceptMeal,
  hydrateMealPlanFromCloud,
  mergeMealPlanFromLiveSync,
  setSlotEntry,
  clearSlot,
  swapSlots,
  replaceSlotRecipe,
  restoreWeek,
} = mealPlanSlice.actions;

// Export helper functions for use in other parts of the app
export { getCurrentMondayStart, getTodayStart, getWeekStartForDate };

// ============== SELECTORS ==============

// Type for the root state (will be properly typed when integrated with store)
interface RootState {
  mealPlan: MealPlanState;
  /**
   * Read only by `selectMealPlanHydrated`, to tell a guest (no cloud copy, the
   * disk is the whole truth) from a signed-in cook (must wait for Firestore).
   * Optional so the many `{ mealPlan }`-only literals in tests still typecheck.
   */
  auth?: {
    user?: { uid?: string | null } | null;
    isAuthenticated?: boolean;
    isGuest?: boolean;
  } | null;
}

/**
 * Select the currently viewed week start date
 */
export const selectCurrentWeekStart = (state: RootState): string =>
  state.mealPlan.currentWeekStart;

/**
 * Inclusive end date (YYYY-MM-DD) of the 7-day window starting at `weekStart`.
 */
const weekEndDate = (weekStart: string): string => addDays(weekStart, 6);

/**
 * Collect meals falling in an inclusive date range. Deliberately scans every
 * week bucket rather than reading `weeks[weekStart]`: historical writes could
 * file a meal under a bucket that doesn't match its `scheduledFor`, and the
 * week views must still show it.
 *
 * FILTER LOCATION RATIONALE: Filtering `mode !== 'proposed'` here (one choke point)
 * inverts the failure mode from "a proposal silently becomes dinner and enters the
 * grocery list" (invisible, costly) to "a proposal fails to appear where it should"
 * (visible, harmless, cheap to fix). Filtering at each call site means a missed site
 * causes a proposal to silently leak into grocery lists, cooking plans, week generators.
 * A single filter here is the only way to guarantee no leakage.
 */
const collectMealsInRange = (
  weeks: Record<string, PlannedMeal[]>,
  startDate: string,
  endDate: string
): PlannedMeal[] =>
  Object.values(weeks)
    .flat()
    .filter(m => m.scheduledFor >= startDate && m.scheduledFor <= endDate)
    .filter(m => m.mode !== 'proposed');

/**
 * Select all meals for the currently viewed week.
 *
 * Memoized: this is read by ~13 components plus an app-wide hook, so an
 * unmemoized version returned a fresh array on every call and re-rendered all
 * of them on every dispatched action anywhere in the app.
 */
export const selectCurrentWeekMeals = createSelector(
  [
    (state: RootState) => state.mealPlan.weeks,
    (state: RootState) => state.mealPlan.currentWeekStart,
  ],
  (weeks, currentWeekStart): PlannedMeal[] =>
    collectMealsInRange(weeks, currentWeekStart, weekEndDate(currentWeekStart))
);

/**
 * The meals of the phone planner's plan: the current Monday week plus the
 * following Monday buckets while they hold meals (#722 stacks them in one
 * scroll). The translation hook reads this, not `selectCurrentWeekMeals`, so a
 * dinner in next week's bucket is translated too (#737). Since #769 the scroll
 * also draws empty weeks and the weeks the cook scrolls into, which this does
 * not follow: a dinner past an empty week shows its title through
 * `usePlannedMealTitle` (`translatedTitles`), not through this hook.
 */
export const selectPlannerHorizonMeals = createSelector(
  [
    (state: RootState) => state.mealPlan.weeks,
    (state: RootState) => state.mealPlan.currentWeekStart,
  ],
  (weeks, currentWeekStart): PlannedMeal[] => {
    let end = weekEndDate(currentWeekStart);
    for (let n = 1; n < 4; n++) {
      const bucket = addDays(currentWeekStart, 7 * n);
      const meals = weeks[bucket] ?? [];
      if (!meals.some(m => m.mode !== 'proposed')) break;
      end = weekEndDate(bucket);
    }
    return collectMealsInRange(weeks, currentWeekStart, end);
  }
);

/**
 * Select all meals (accepted + proposed) for the currently viewed week.
 * Used only by the two v2 proposal grids; all other consumers should use
 * `selectCurrentWeekMeals`, which excludes proposals.
 *
 * Memoized with the same shape as `selectCurrentWeekMeals` to ensure
 * efficient re-renders when the selector is called from a component
 * that needs proposals.
 */
export const selectCurrentWeekMealsWithProposals = createSelector(
  [
    (state: RootState) => state.mealPlan.weeks,
    (state: RootState) => state.mealPlan.currentWeekStart,
  ],
  (weeks, currentWeekStart): PlannedMeal[] => {
    const startDate = currentWeekStart;
    const endDate = weekEndDate(currentWeekStart);
    return Object.values(weeks)
      .flat()
      .filter(m => m.scheduledFor >= startDate && m.scheduledFor <= endDate);
  }
);

/**
 * Select all meals for an arbitrary week, by that week's start date.
 *
 * Memoized per `weekStart` (not a single-entry cache) so callers watching
 * different weeks — e.g. the viewed week and the grocery list's pinned week —
 * don't evict each other and thrash back to fresh arrays.
 */
const mealsForWeekSelectors = new Map<string, (state: RootState) => PlannedMeal[]>();
const MAX_WEEK_SELECTORS = 32;

export const selectMealsForWeek = (weekStart: string) => {
  let selector = mealsForWeekSelectors.get(weekStart);
  if (!selector) {
    // Bound the cache: a long session browsing many weeks would otherwise
    // accumulate one selector per week visited, forever.
    if (mealsForWeekSelectors.size >= MAX_WEEK_SELECTORS) {
      mealsForWeekSelectors.clear();
    }
    selector = createSelector(
      [(state: RootState) => state.mealPlan.weeks],
      (weeks): PlannedMeal[] =>
        collectMealsInRange(weeks, weekStart, weekEndDate(weekStart))
    );
    mealsForWeekSelectors.set(weekStart, selector);
  }
  return selector;
};

/**
 * One memoized selector per date range, bounded like `selectMealsForWeek`.
 *
 * #668: the range selectors below returned a fresh array on every call, and
 * three screens read them through `useSelector`, so each re-rendered on every
 * dispatched action anywhere in the app and re-flattened every week bucket.
 */
const mealsForRangeSelectors = new Map<string, (state: RootState) => PlannedMeal[]>();
const MAX_RANGE_SELECTORS = 64;

const rangeSelector = (startDate: string, endDate: string) => {
  const key = `${startDate}|${endDate}`;
  let selector = mealsForRangeSelectors.get(key);
  if (!selector) {
    if (mealsForRangeSelectors.size >= MAX_RANGE_SELECTORS) {
      mealsForRangeSelectors.clear();
    }
    selector = createSelector(
      [(state: RootState) => state.mealPlan.weeks],
      (weeks): PlannedMeal[] => collectMealsInRange(weeks, startDate, endDate)
    );
    mealsForRangeSelectors.set(key, selector);
  }
  return selector;
};

/**
 * Select all meals within an inclusive date range, spanning any number of week buckets.
 */
export const selectMealsForDateRange = (
  state: RootState,
  startDate: string,
  endDate: string
): PlannedMeal[] => rangeSelector(startDate, endDate)(state);

/**
 * Select meals for a specific week by start date. Routes through `collectMealsInRange`
 * to catch meals that might be filed in different week buckets due to historical writes.
 */
export const selectWeekByStartDate = (state: RootState, weekStartDate: string): PlannedMeal[] => {
  // Unlike the other callers of weekEndDate, this one's argument comes straight
  // off a URL param (/week-recap?week=...), so it can be absent or malformed.
  // weekEndDate would then build an Invalid Date and throw RangeError out of a
  // selector — i.e. during render, taking the whole screen to the error
  // boundary. week-recap's own `!weekStartDate` guard cannot help: it sits
  // below the useSelector call that crashes.
  // A calendar-day string is the contract here, not just anything Date accepts:
  // '2026-09' parses fine and would silently select the week of the 1st, and
  // the range comparison downstream is lexicographic on YYYY-MM-DD.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekStartDate)) return EMPTY_MEALS;
  if (Number.isNaN(new Date(weekStartDate + 'T12:00:00').getTime())) return EMPTY_MEALS;
  return rangeSelector(weekStartDate, weekEndDate(weekStartDate))(state);
};

/**
 * Select meals for a specific date. Memoized per date on that date's week
 * bucket, so editing another week does not invalidate it.
 */
const mealsByDateSelectors = new Map<string, (state: RootState) => PlannedMeal[]>();
const MAX_DATE_SELECTORS = 64;
const EMPTY_MEALS: PlannedMeal[] = [];

export const selectMealsByDate = (state: RootState, date: string): PlannedMeal[] => {
  let selector = mealsByDateSelectors.get(date);
  if (!selector) {
    if (mealsByDateSelectors.size >= MAX_DATE_SELECTORS) {
      mealsByDateSelectors.clear();
    }
    const weekStart = getWeekStartForDate(date);
    selector = createSelector(
      [(s: RootState) => s.mealPlan.weeks[weekStart] || EMPTY_MEALS],
      (weekMeals): PlannedMeal[] => weekMeals.filter(meal => meal.scheduledFor === date)
    );
    mealsByDateSelectors.set(date, selector);
  }
  return selector(state);
};

/**
 * Select a specific meal by ID
 */
export const selectMealById = (state: RootState, mealId: string): PlannedMeal | undefined => {
  for (const weekStart of Object.keys(state.mealPlan.weeks)) {
    const meal = state.mealPlan.weeks[weekStart].find(m => m.id === mealId);
    if (meal) return meal;
  }
  return undefined;
};

/**
 * Select all entries of a given recipe within a specific week
 */
export const selectRecipeEntriesInWeek = (
  state: RootState,
  recipeId: string,
  weekStartDate: string,
): PlannedMeal[] => {
  const weekMeals = state.mealPlan.weeks[weekStartDate] || [];
  return weekMeals.filter(meal => String(meal.recipeId) === String(recipeId));
};

/**
 * Select all meals with unreviewed ingredients for the current week
 */
export const selectUnreviewedMeals = createSelector(
  [selectCurrentWeekMeals],
  (currentWeekMeals): PlannedMeal[] =>
    currentWeekMeals.filter(meal => !meal.ingredientsReviewed && meal.recipeId !== 'free-text')
);

/**
 * Select all missing ingredients from reviewed meals for the current week
 */
export const selectAllMissingIngredients = createSelector(
  [selectCurrentWeekMeals],
  (currentWeekMeals): { ingredient: string; mealId: string; mealTitle: string }[] => {
    const result: { ingredient: string; mealId: string; mealTitle: string }[] = [];

    currentWeekMeals.forEach(meal => {
      if (meal.recipeId === 'free-text') return; // Skip free-text meals
      if (meal.ingredientsReviewed && meal.missingIngredients.length > 0) {
        meal.missingIngredients.forEach(ingredient => {
          result.push({
            ingredient,
            mealId: meal.id,
            mealTitle: meal.recipe?.title || 'Unknown Recipe',
          });
        });
      }
    });

    return result;
  }
);

/**
 * Check if a week has any meals planned
 */
export const selectWeekHasMeals = (state: RootState, weekStartDate: string): boolean => {
  const meals = state.mealPlan.weeks[weekStartDate];
  return meals !== undefined && meals.length > 0;
};

/**
 * Whether an empty week can be believed.
 *
 * Every meal-plan selector returns a bare array, so "this week has no meals"
 * and "this week has not loaded" are indistinguishable at the call site. Any
 * caller about to act destructively on emptiness — the planner's auto-generate
 * is the only one today — must check this first.
 *
 * A guest has no cloud copy, so the disk is the whole truth. Same when Firebase
 * is off: nothing will ever settle the cloud half, and gating on it would mean
 * the planner never generates at all.
 */
export const selectMealPlanHydrated = (state: RootState): boolean => {
  if (!state.mealPlan.localHydrated) return false;

  const auth = state.auth;
  const signedIn = Boolean(auth?.isAuthenticated && !auth?.isGuest && auth?.user?.uid);
  if (!signedIn || !isFirebaseEnabled()) return true;

  return state.mealPlan.cloudHydrated;
};

/**
 * Get the previous week's recipe IDs (for avoiding repeats in generation)
 */
export const selectPreviousWeekRecipeIds = createSelector(
  [
    (state: RootState) => state.mealPlan.weeks,
    (state: RootState) => state.mealPlan.currentWeekStart,
  ],
  (weeks, currentWeekStart): string[] => {
    const previousWeekMeals = weeks[addDays(currentWeekStart, -7)] || [];
    return previousWeekMeals.map(meal => meal.recipeId);
  }
);

export const selectRecipeCookHistory = createSelector(
  [(state: RootState) => state.mealPlan?.weeks, (_: RootState, recipeId: string) => recipeId],
  (weeks, recipeId): Array<{ mealId: string; cookedAt: string; rating?: number; cookNote?: string; cookNoteBy?: string }> => {
    if (!weeks) return [];
    return Object.values(weeks)
      .flat()
      .filter(m => m.recipeId === recipeId && m.status === 'cooked' && !!m.cookedAt)
      .map(m => ({ mealId: m.id, cookedAt: m.cookedAt!, rating: m.rating, cookNote: m.cookNote, cookNoteBy: m.cookNoteBy }))
      .sort((a, b) => b.cookedAt.localeCompare(a.cookedAt));
  }
);

/**
 * All planned meals (across every week) for a given recipe, most recently
 * scheduled first. Used to bridge a "I cooked this" log from the recipe detail
 * screen back onto the matching planned meal so it surfaces in meal planning.
 */
export const selectPlannedMealsForRecipe = createSelector(
  [(state: RootState) => state.mealPlan?.weeks, (_: RootState, recipeId: string) => recipeId],
  (weeks, recipeId): PlannedMeal[] => {
    if (!weeks) return [];
    return Object.values(weeks)
      .flat()
      .filter(m => String(m.recipeId) === String(recipeId))
      .sort((a, b) => (b.scheduledFor || '').localeCompare(a.scheduledFor || ''));
  }
);

/**
 * Decide which planned meal (if any) a "I cooked this" log should be bridged
 * onto. Prefers an uncooked meal scheduled on the chosen date; otherwise the
 * most recently scheduled uncooked meal. Returns undefined when the recipe has
 * no uncooked planned meal (caller then records a standalone cook-log entry).
 *
 * `plannedMeals` is expected most-recent-first (see selectPlannedMealsForRecipe).
 */
export function pickMealToBridgeCook(
  plannedMeals: PlannedMeal[],
  isoDate: string,
): PlannedMeal | undefined {
  const uncooked = plannedMeals.filter(m => m.status !== 'cooked');
  return uncooked.find(m => m.scheduledFor === isoDate) ?? uncooked[0];
}

export const selectMostRecentScheduledDate = createSelector(
  [(state: RootState) => state.mealPlan?.weeks, (_: RootState, recipeId: string) => recipeId],
  (weeks, recipeId): string | undefined => {
    if (!weeks) return undefined;
    const dates = Object.values(weeks)
      .flat()
      .filter(m => m.recipeId === recipeId && !!m.scheduledFor)
      .map(m => m.scheduledFor)
      .sort();
    return dates.at(-1);
  }
);

/**
 * Get freshness label for a recipe
 * Returns: "First time!" | "Last week" | "2 weeks ago" | "Last month" | etc.
 */
export function getRecipeFreshnessLabel(lastUsedDate: string | null): string {
  if (!lastUsedDate) return "First time!";

  const lastUsed = new Date(lastUsedDate + 'T12:00:00');
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - lastUsed.getTime()) / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffWeeks <= 1) return "Last week";
  if (diffWeeks <= 4) return `${diffWeeks} weeks ago`;
  if (diffWeeks <= 8) return "Last month";
  return "A while ago";
}

export default mealPlanSlice.reducer;
